import type { TFunction } from 'i18next';

const COMMUNITY_PATH = 'community';
const COMMUNITY_REMIX_ROUTE_BASE = `routes/${COMMUNITY_PATH}`;

export const FEED_PANEL_ID = 'community-feed-panel';

export const COMMUNITY_SIDEBAR_WIDTH_CLASS = 'w-[var(--community-sidebar-width)]';
export const COMMUNITY_FEED_OVERLAY_OFFSET_CLASS = 'md:left-[var(--community-sidebar-width)]';
const COMMUNITY_FEED_TOAST_OFFSET_CLASS = 'md:!left-[calc(50%_+_var(--community-sidebar-width)_/_2)]';

export const communitySidebarWidthStyle = (navigationV2Enabled: boolean): string =>
  `:root { --community-sidebar-width: ${navigationV2Enabled ? '256px' : '230px'}; }`;

export const COMMUNITY_ROUTES = {
  root: `/${COMMUNITY_PATH}`,
  banned: `/${COMMUNITY_PATH}/banned`,
  messages: `/${COMMUNITY_PATH}/messages`,
  events: `/${COMMUNITY_PATH}/events`,
  notifications: `/${COMMUNITY_PATH}/notifications`,
  onboard: `/${COMMUNITY_PATH}/onboard`,
  search: `/${COMMUNITY_PATH}/search`,
  spaces: (slug: string) => `/${COMMUNITY_PATH}/channels/${slug}`,
  post: (id: string) => `/${COMMUNITY_PATH}/posts/${id}`,
  member: (id: string) => `/${COMMUNITY_PATH}/members/${id}`,
  page: (slug: string) => `/${COMMUNITY_PATH}/pages/${slug}`,
  pageResource: (pageSlug: string, resourceSlug: string) => `/${COMMUNITY_PATH}/pages/${pageSlug}/${resourceSlug}`,
  chatDm: (memberId: string) => `/${COMMUNITY_PATH}/messages/dm/${memberId}`,
  chatGroup: (chatId: string) => `/${COMMUNITY_PATH}/messages/group/${chatId}`,
  memberSelf: `/${COMMUNITY_PATH}/members/me`,
  memberEdit: `/${COMMUNITY_PATH}/members/me/edit`,
  memberSettings: `/${COMMUNITY_PATH}/members/me/settings`,
  blockedMembers: `/${COMMUNITY_PATH}/members/me/blocked`,
  directory: `/${COMMUNITY_PATH}/directory`,
} as const;

export const communityToasterContainerClassName = (pathname: string): string | undefined => {
  const normalized = pathname.replace(/\/$/, '');
  const isCommunityPage = normalized === COMMUNITY_ROUTES.root || normalized.startsWith(`${COMMUNITY_ROUTES.root}/`);
  if (!isCommunityPage || normalized === COMMUNITY_ROUTES.onboard || normalized === COMMUNITY_ROUTES.banned) {
    return undefined;
  }
  return COMMUNITY_FEED_TOAST_OFFSET_CLASS;
};

/** First segment of every directory React Query key, so one prefix invalidates them all. */
export const COMMUNITY_DIRECTORY_CACHE_PREFIX = 'community-directory';

export const isDirectoryRoute = (pathname: string): boolean =>
  pathname.replace(/\/$/, '') === COMMUNITY_ROUTES.directory;

export const isMessagesRoute = (pathname: string): boolean =>
  pathname === COMMUNITY_ROUTES.messages || pathname.startsWith(`${COMMUNITY_ROUTES.messages}/`);

export const isContentPageRoute = (pathname: string): boolean => {
  const [, root, section, pageSlug, resourceSlug] = pathname.split('/');
  return root === COMMUNITY_PATH && section === 'pages' && Boolean(pageSlug) && !resourceSlug;
};

/** The bare inbox URL (no conversation selected), tolerating a trailing slash. */
export const isMessagesIndexRoute = (pathname: string): boolean =>
  pathname.replace(/\/$/, '') === COMMUNITY_ROUTES.messages;

export const isCommunitySpacePath = (pathname: string): boolean =>
  pathname.startsWith(`${COMMUNITY_ROUTES.root}/channels/`);

/** The aggregated feed root or a channel feed — the post-list pages that share the feed skeleton. */
export const isCommunityFeedPath = (pathname: string): boolean => {
  const normalized = pathname.replace(/\/$/, '');
  return normalized === COMMUNITY_ROUTES.root || normalized.startsWith(`${COMMUNITY_ROUTES.root}/channels/`);
};

/** Remix `useMatches()` route ids for community pages (file-based, not URL paths). */
export const COMMUNITY_ROUTE_IDS = {
  layout: COMMUNITY_REMIX_ROUTE_BASE,
  feed: `${COMMUNITY_REMIX_ROUTE_BASE}/index`,
  messages: `${COMMUNITY_REMIX_ROUTE_BASE}/messages/index`,
  events: `${COMMUNITY_REMIX_ROUTE_BASE}/events/index`,
  directory: `${COMMUNITY_REMIX_ROUTE_BASE}/directory/index`,
  notifications: `${COMMUNITY_REMIX_ROUTE_BASE}/notifications/index`,
  onboard: `${COMMUNITY_REMIX_ROUTE_BASE}/onboard/index`,
  space: `${COMMUNITY_REMIX_ROUTE_BASE}/channels/$slug/index`,
  post: `${COMMUNITY_REMIX_ROUTE_BASE}/posts/$id/index`,
  contentPage: `${COMMUNITY_REMIX_ROUTE_BASE}/pages/$slug/index`,
  contentPageResource: `${COMMUNITY_REMIX_ROUTE_BASE}/pages/$slug/$resourceSlug/index`,
} as const;

export const isCommunitySpaceRoute = (routeId: string): boolean =>
  routeId.startsWith(`${COMMUNITY_REMIX_ROUTE_BASE}/channels/`);

export const POST_SORT_VALUES = ['recent'] as const;
export const SORT_ENABLED = POST_SORT_VALUES.length > 1;
export type PostSortValue = (typeof POST_SORT_VALUES)[number];
export const POST_SORT_DEFAULT: PostSortValue = 'recent';

export const FEED_VIEW_DEFAULT = 'for_you';
export const FEED_VIEWS = [FEED_VIEW_DEFAULT, 'following'] as const;
export type FeedView = (typeof FEED_VIEWS)[number];

// Which page a paid gate covers, not the tier that locked it.
export const GATE_SCOPES = {
  Post: 'post',
  Channel: 'channel',
  Community: 'community',
} as const;
export type GateScope = (typeof GATE_SCOPES)[keyof typeof GATE_SCOPES];

export const normalizeFeedView = (value: string | null | undefined): FeedView =>
  FEED_VIEWS.includes(value as FeedView) ? (value as FeedView) : FEED_VIEW_DEFAULT;

export type PostSortOption = { value: PostSortValue; label: string };

export const getPostSortOptions = (t: TFunction): [PostSortOption, ...PostSortOption[]] =>
  POST_SORT_VALUES.map((value) => ({
    value,
    label: t(`community.sort.${value}`),
  })) as [PostSortOption, ...PostSortOption[]];

export const subscriptionManagePath = (subscriberId: string): string => `/subscribe/${subscriberId}/manage`;

export const subscriptionBillingPath = (subscriberId: string): string => `/subscribe/${subscriberId}/billing`;
